/**
 * Per-criterion weight multipliers — the "×2" the stats and spells panels show
 * next to an active criterion when the weights column is on.
 *
 * A weight is a *multiplier* on the scoring weight a criterion already has
 * (`st.STAT_WEIGHTS_MAP` for characteristics), never the weight itself: ×1 is
 * "leave it as it always was", which is why only entries that differ from 1 are
 * ever stored. `Build.criteriaWeights` is one flat map keyed by the helpers
 * below, so stats, spell lines and the weapon all share a single field instead
 * of three parallel ones.
 */

/**
 * Multiplier bounds, ×0.1 to ×10. 0 is excluded at the bottom: a criterion
 * nothing scores is a criterion the user should remove instead. The ceiling is
 * a decade because these multiply weights that already span three orders of
 * magnitude (AP is 100, Initiative is 0.1) — past ×10 a single criterion simply
 * swamps the rest of the sheet, which "maximize" already expresses.
 */
export const WEIGHT_MIN = 0.1;
export const WEIGHT_MAX = 10;
export const WEIGHT_DEFAULT = 1;

/**
 * Stable identity of a spell-line criterion (spell id + the effect lines it
 * targets), used both to match a selection against a rendered row and as the
 * key a weight is stored under.
 */
export function spellLineCriterionSignature(criterion) {
    if (!criterion) return null;
    const spellId = Number(criterion.spell_id ?? criterion.spellId ?? criterion.id);
    if (!Number.isFinite(spellId)) return null;
    const keys = Array.from(new Set(
        (criterion.line_keys ?? criterion.lineKeys ?? [])
            .map((x) => String(x || '').trim())
            .filter(Boolean)
    )).sort();
    if (!keys.length) return null;
    return `${spellId}|${keys.join('||')}`;
}

export const statWeightKey = (statId) => `stat:${Number(statId)}`;

/** True for a stat's weight key; spell lines and the weapon are the spells tab's. */
export const isStatWeightKey = (key) => String(key).startsWith('stat:');

export const WEAPON_WEIGHT_KEY = 'weapon';

/** Weight key for a spell criterion — line-keyed, or a bare id for legacy whole-spell criteria. */
export function spellWeightKey(criterion) {
    if (criterion == null) return null;
    if (typeof criterion === 'number') {
        return Number.isFinite(criterion) ? `spell:${criterion}` : null;
    }
    const sig = spellLineCriterionSignature(criterion);
    if (sig) return `spell:${sig}`;
    const spellId = Number(criterion.spell_id ?? criterion.spellId ?? criterion.id);
    return Number.isFinite(spellId) ? `spell:${spellId}` : null;
}

/** Clamp to the allowed range and to one decimal; anything unparseable is ×1. */
export function normalizeWeight(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return WEIGHT_DEFAULT;
    const clamped = Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, n));
    return Math.round(clamped * 10) / 10;
}

/** The number alone ("1", "1.5") — the input's text, without the × sign. */
export function formatWeightValue(value) {
    return String(normalizeWeight(value));
}

/** Keep only well-formed, non-default entries: ×1 is the absence of an entry. */
export function normalizeWeightMap(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    Object.entries(raw).forEach(([key, value]) => {
        if (!key) return;
        const n = Number(value);
        if (!Number.isFinite(n)) return;
        const weight = normalizeWeight(n);
        if (weight === WEIGHT_DEFAULT) return;
        out[key] = weight;
    });
    return out;
}

/**
 * Drop weights whose criterion no longer exists. Called at the storage boundary
 * rather than on every Build: several criteria edits are two builds long (∞ →
 * numeric target is `setMaximizeStat(false)` then `setDesired`), and pruning in
 * between would throw the weight away mid-move.
 *
 * Duck-typed on purpose — importing Build here would close a cycle.
 */
export function pruneCriteriaWeights(build) {
    const weights = build?.criteriaWeights;
    if (!weights || Object.keys(weights).length === 0) return {};
    const live = new Set();
    Object.keys(build.getSolverTargets?.() || {}).forEach((id) => live.add(statWeightKey(id)));
    (build.getMaximizeSpellCriteria?.() || []).forEach((criterion) => {
        const key = spellWeightKey(criterion);
        if (key) live.add(key);
    });
    if (build.maximizeWeapon || build.maximizeBestWeapon) live.add(WEAPON_WEIGHT_KEY);
    return Object.fromEntries(
        Object.entries(weights).filter(([key]) => live.has(key))
    );
}
